import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import { NotificationContext } from '../../../../providers/notification';
import apiCall from '../../../../utils/apiCall';

interface Payload {
    CountryId: string;
    StateName: string;
}

interface Country {
    CountryId: number;
    CountryName: string;
}
  
interface CountryResponse {
    data: Country[];
}

interface ComponentProps {
    stateId: number;
    countryId: number;
    stateName: string;
    handleClose: () => void;
    data: CountryResponse | undefined;
    isLoading: boolean;
    isError: boolean;
    errorMessage: string;
}

const UpdateState: React.FC<ComponentProps> = ({ handleClose, stateId, stateName, data, isError, isLoading, errorMessage, countryId }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const updateState = (data: Payload) => {
        return apiCall().put(`/State/updateState/${stateId}`, data);
    };

    const updateStateMutation = useMutation(['update-state'], updateState, {retry: 0});

    const updateStateHandler = async (values: FormikValues) => {
        const payload: Payload = {
            CountryId: values?.country,
            StateName: values?.state,
        };

        try {
            await updateStateMutation.mutateAsync(payload, {
                onSuccess: () => {
                    showNotification({
                        message: 'State successfully updated.',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-states']);
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        country: Yup.string().required('required'),
        state: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            country: countryId ? countryId : '',
            state: stateName ? stateName : '',
        },
        onSubmit: (values) => {
            updateStateHandler(values);
        },
        validationSchema
    });

    const countryOptions = data && data?.data.length > 0 ? (
        data?.data.map((country: Country) => (
            <option key={country.CountryId} value={country.CountryId}>
                {country.CountryName}
            </option>
        ))
    ) : (
        <option disabled>
            {isLoading ? "Loading..." : isError ? errorMessage : ''}
        </option>
    )

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                <Field 
                    name='country'
                    as={Select}
                    label='Country'
                    options={countryOptions}
                />
                <Field 
                    name='state'
                    type='text'
                    as={Input}
                    label='State'
                    placeholder='State'
                />
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={updateStateMutation.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={updateStateMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default UpdateState;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

